import { useTranslation } from 'react-i18next';
import { traduzirTarefas as tt } from '../pages/Tarefas/tarefasI18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  EVENTO_PLAY,
  avisarPlayAlterado,
  formatarCronometro,
  pausarPlay,
  temAcessoTarefas
} from '../pages/Tarefas/tarefasUtils';
import { IconePausa } from '../pages/Tarefas/componentes';
import '../styles/Tarefas.css';

// Com play rodando, curto para quem tem o play encerrado por outra pessoa (tarefa
// concluida pelo lider, por exemplo) ver a parada logo. Sem play, a consulta so
// serve para notar um play ligado em outra aba ou aparelho. So consulta com a
// aba visivel.
export const INTERVALO_COM_PLAY_MS = 5000;
export const INTERVALO_SEM_PLAY_MS = 30000;

// Voltar para a aba dispara `focus` e `visibilitychange` juntos.
const JANELA_VOLTA_MS = 1000;

// Cronometro do play ativo na barra superior, visivel em qualquer pagina
// (como no runrun.it). So aparece para quem usa o modulo de Tarefas e tem um
// play rodando.
const PlayAtivo = ({ usuario }) => {
  useTranslation('tasks');
  const navigate = useNavigate();
  const habilitado = temAcessoTarefas(usuario);
  const [ativo, setAtivo] = useState(null);
  const [agora, setAgora] = useState(Date.now());
  // Apontamento visto na ultima consulta (undefined = ainda nao consultou).
  const ultimoApontamentoRef = useRef(undefined);
  const ultimaConsultaRef = useRef(0);
  const consultaAtualRef = useRef(0);
  // O aviso abaixo e sincrono: enquanto ele roda, o evento e o nosso proprio.
  const avisandoRef = useRef(false);

  const carregar = useCallback(async () => {
    consultaAtualRef.current += 1;
    const consulta = consultaAtualRef.current;
    ultimaConsultaRef.current = Date.now();
    let novo;
    try {
      const { data } = await api.get('/tarefas/play', { skipAuthRedirect: true });
      novo = data?.ativo || null;
    } catch (_err) {
      // Falha de rede nao e "sem play": mantem o cronometro como esta.
      return;
    }
    // Uma consulta mais nova saiu depois desta: a resposta dela e que vale.
    if (consulta !== consultaAtualRef.current) return;
    setAtivo(novo);
    // O play mudou sem ter sido por esta aba: avisa as listas e o painel.
    const anterior = ultimoApontamentoRef.current;
    ultimoApontamentoRef.current = novo?.id ?? null;
    if (anterior !== undefined && anterior !== ultimoApontamentoRef.current) {
      avisandoRef.current = true;
      avisarPlayAlterado();
      avisandoRef.current = false;
    }
  }, []);

  // Primeira consulta e o que muda fora do intervalo: outras telas e a volta
  // para a aba.
  useEffect(() => {
    if (!habilitado) return undefined;
    const aoMudarPlay = () => { if (!avisandoRef.current) carregar(); };
    const aoVoltar = () => {
      if (document.hidden) return;
      if (Date.now() - ultimaConsultaRef.current < JANELA_VOLTA_MS) return;
      carregar();
    };
    carregar();
    window.addEventListener(EVENTO_PLAY, aoMudarPlay);
    window.addEventListener('focus', aoVoltar);
    document.addEventListener('visibilitychange', aoVoltar);
    return () => {
      window.removeEventListener(EVENTO_PLAY, aoMudarPlay);
      window.removeEventListener('focus', aoVoltar);
      document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, [habilitado, carregar]);

  // Depende so de haver play: as consultas trazem um objeto novo a cada vez e,
  // se recriassem o intervalo, o tique de 1 s seria perdido e o tempo pularia.
  const rodando = Boolean(ativo);

  useEffect(() => {
    if (!habilitado) return undefined;
    const periodo = rodando ? INTERVALO_COM_PLAY_MS : INTERVALO_SEM_PLAY_MS;
    let intervalo = null;
    const iniciar = () => { if (!intervalo) intervalo = setInterval(carregar, periodo); };
    const parar = () => { clearInterval(intervalo); intervalo = null; };
    const aoMudarVisibilidade = () => { if (document.hidden) parar(); else iniciar(); };
    if (!document.hidden) iniciar();
    document.addEventListener('visibilitychange', aoMudarVisibilidade);
    return () => {
      parar();
      document.removeEventListener('visibilitychange', aoMudarVisibilidade);
    };
  }, [habilitado, rodando, carregar]);

  useEffect(() => {
    if (!rodando) return undefined;
    setAgora(Date.now());
    const tique = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(tique);
  }, [rodando]);

  if (!habilitado || !ativo) return null;

  const segundos = (agora - new Date(ativo.inicio).getTime()) / 1000;

  return (
    <div className="play-ativo" title={`${ativo.projeto_nome} — #${ativo.numero} ${ativo.titulo}`}>
      <span className="play-ativo-pulso" aria-hidden="true" />
      <button
        type="button"
        className="play-ativo-titulo"
        onClick={() => navigate(`/tarefas?tarefa=${ativo.tarefa_id}`)}
      >
        #{ativo.numero} {ativo.titulo}
      </button>
      <span className="play-ativo-tempo">{formatarCronometro(segundos)}</span>
      <button
        type="button"
        className="play-ativo-pausar"
        onClick={async () => {
          // Deu certo: o evento de pausarPlay ja faz a consulta.
          try {
            await pausarPlay();
          } catch (_err) {
            carregar();
          }
        }}
        aria-label={tt('pause')}
        title={tt('pause')}
      >
        <IconePausa />
      </button>
    </div>
  );
};

export default PlayAtivo;
